import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';

import { CrossDomainStorageService } from './cross-domain-storage.service';

interface LoginAttempt {
  email: string;
  timestamp: number;
  success: boolean;
}

interface AttemptData {
  attempts: LoginAttempt[];
  firstAttemptTime: number;
  suspiciousActivityLogged: boolean;
}

interface StorageData {
  [email: string]: AttemptData;
}

@Injectable({
  providedIn: 'root'
})
export class AuthAttemptTrackerService {
  private readonly STORAGE_KEY = 'auth_attempt_tracker_v1';
  private readonly MAX_FAILED_ATTEMPTS = 3;
  private readonly ATTEMPT_WINDOW_MS = 5 * 60 * 1000; // 5 minutos
  private readonly CLEANUP_INTERVAL_MS = 15 * 60 * 1000; // 15 minutos
  private readonly MAX_TRACKED_EMAILS = 50;

  private cleanupTimer: any = null;

  constructor(private storage: CrossDomainStorageService) {
    this.startCleanupTimer();
  }

  /**
   * Registra uma tentativa de login falhada. A Promise resolve após a
   * gravação, para o threshold do alerta poder contar esta falha.
   */
  public recordFailedAttempt(email: string): Promise<void> {
    return this.loadAllAttempts().then(allData => {
      const data = this.getOrCreateAttemptData(allData, email);

      data.attempts.push({
        email,
        timestamp: Date.now(),
        success: false
      });

      allData[email] = data;
      return this.saveAllAttempts(allData);
    }).catch(error => {
      console.error('Error recording failed attempt:', error);
    });
  }

  /**
   * Registra uma tentativa de login bem-sucedida e limpa o histórico
   */
  public recordSuccessfulAttempt(email: string): void {
    this.clearAttempts(email);
  }

  /**
   * Registra uma falha de login (medida no gate real: 4xx do backend) e, ao
   * atingir o threshold, captura o alerta de força bruta no Sentry.
   * Fire-and-forget: o caller não deve aguardar esta Promise no fluxo do
   * botão "Entrando..." — os awaits internos dependem do storage cross-domain.
   */
  public async trackFailure(email: string): Promise<void> {
    try {
      // Aguarda a gravação para o threshold contar ESTA falha (sem isso o
      // alerta só dispararia na tentativa seguinte). Concorrência entre abas
      // continua best-effort: o storage cross-domain não tem operação atômica.
      await this.recordFailedAttempt(email);

      if (await this.shouldLogToSentry(email)) {
        const attemptCount = await this.getRecentAttemptCount(email);
        Sentry.captureMessage('Múltiplas tentativas de login falhadas', {
          level: 'warning',
          tags: {
            component: 'auth-api',
            action: 'login',
            security_alert: 'multiple-failed-attempts',
          },
          extra: {
            email,
            failed_attempt_count: attemptCount,
            time_window_minutes: 5,
          },
        });
      }
    } catch (error) {
      console.error('Error tracking failed login:', error);
    }
  }

  /**
   * Verifica se devemos logar no Sentry (threshold atingido)
   */
  public async shouldLogToSentry(email: string): Promise<boolean> {
    try {
      const allData = await this.loadAllAttempts();
      const data = allData[email];

      if (!data) {
        return false;
      }

      // Já logamos esta sessão de tentativas?
      if (data.suspiciousActivityLogged) {
        return false;
      }

      // Verificar se threshold foi atingido
      const recentFailures = this.countRecentFailedAttempts(data);
      if (recentFailures >= this.MAX_FAILED_ATTEMPTS) {
        // Marcar como logado para evitar spam
        data.suspiciousActivityLogged = true;
        allData[email] = data;
        await this.saveAllAttempts(allData);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error checking Sentry threshold:', error);
      return false;
    }
  }

  /**
   * Obtém a contagem de tentativas recentes falhadas
   */
  public async getRecentAttemptCount(email: string): Promise<number> {
    try {
      const allData = await this.loadAllAttempts();
      const data = allData[email];

      if (!data) {
        return 0;
      }

      return this.countRecentFailedAttempts(data);
    } catch (error) {
      console.error('Error getting attempt count:', error);
      return 0;
    }
  }

  /**
   * Limpa as tentativas para um email específico
   */
  public clearAttempts(email: string): void {
    this.loadAllAttempts().then(allData => {
      delete allData[email];
      this.saveAllAttempts(allData);
    }).catch(error => {
      console.error('Error clearing attempts:', error);
    });
  }

  /**
   * Conta tentativas falhadas recentes dentro da janela de tempo
   */
  private countRecentFailedAttempts(data: AttemptData): number {
    const cutoff = Date.now() - this.ATTEMPT_WINDOW_MS;
    return data.attempts.filter(
      attempt => !attempt.success && attempt.timestamp > cutoff
    ).length;
  }

  /**
   * Obtém ou cria dados de tentativa para um email
   */
  private getOrCreateAttemptData(allData: StorageData, email: string): AttemptData {
    if (allData[email]) {
      return allData[email];
    }

    return {
      attempts: [],
      firstAttemptTime: Date.now(),
      suspiciousActivityLogged: false
    };
  }

  /**
   * Carrega todas as tentativas do storage
   */
  private async loadAllAttempts(): Promise<StorageData> {
    try {
      const data = await this.storage.getItem<StorageData>(this.STORAGE_KEY);
      return data || {};
    } catch (error) {
      console.error('Error loading attempts from storage:', error);
      return {};
    }
  }

  /**
   * Salva todas as tentativas no storage
   */
  private async saveAllAttempts(data: StorageData): Promise<void> {
    try {
      // Aplicar limite de emails rastreados
      const emailCount = Object.keys(data).length;
      if (emailCount > this.MAX_TRACKED_EMAILS) {
        this.evictOldestEntries(data);
      }

      await this.storage.setItem(this.STORAGE_KEY, data);
    } catch (error) {
      console.error('Error saving attempts to storage:', error);
    }
  }

  /**
   * Remove as entradas mais antigas quando o limite é atingido
   */
  private evictOldestEntries(data: StorageData): void {
    const emails = Object.keys(data);
    const emailsWithTime = emails.map(email => ({
      email,
      time: data[email].firstAttemptTime
    }));

    // Ordenar por tempo (mais antigo primeiro)
    emailsWithTime.sort((a, b) => a.time - b.time);

    // Remover 25% das entradas mais antigas
    const toRemove = Math.ceil(emailsWithTime.length * 0.25);
    for (let i = 0; i < toRemove; i++) {
      delete data[emailsWithTime[i].email];
    }
  }

  /**
   * Limpa tentativas antigas de todos os emails
   */
  private cleanupOldAttempts(): void {
    this.loadAllAttempts().then(allData => {
      const cutoff = Date.now() - this.ATTEMPT_WINDOW_MS;
      let hasChanges = false;

      Object.keys(allData).forEach(email => {
        const data = allData[email];
        const originalLength = data.attempts.length;

        // Filtrar tentativas antigas
        data.attempts = data.attempts.filter(
          attempt => attempt.timestamp > cutoff
        );

        // Se não há mais tentativas, remover o email
        if (data.attempts.length === 0) {
          delete allData[email];
          hasChanges = true;
        } else if (data.attempts.length !== originalLength) {
          hasChanges = true;
        }
      });

      if (hasChanges) {
        this.saveAllAttempts(allData);
      }
    }).catch(error => {
      console.error('Error during cleanup:', error);
    });
  }

  /**
   * Inicia o timer de limpeza automática
   */
  private startCleanupTimer(): void {
    // Limpar timer anterior se existir
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }

    // Criar novo timer
    this.cleanupTimer = setInterval(
      () => this.cleanupOldAttempts(),
      this.CLEANUP_INTERVAL_MS
    );
  }

  /**
   * Cleanup quando o serviço é destruído
   */
  ngOnDestroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }
}
